import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import UserOrderView from './components/UserOrderView';
import CashierView from './components/CashierView';
import SalesReport from './components/SalesReport';
import SessionsView from './components/SessionsView';
import Analytics from './components/Analytics';
import ProductManagement from './components/ProductManagement';
import InventoryManagement from './components/InventoryManagement';
import SetupData from './components/SetupData';
import UserRoleManagement from './components/UserRoleManagement';
import Settings from './components/Settings';
import Navbar from './components/Navbar';
import LicenseExpired from './components/LicenseExpired';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { currentUser, licenseStatus } = useAuth();

  // Check license validity first
  if (licenseStatus && !licenseStatus.isValid) {
    return <LicenseExpired licenseStatus={licenseStatus} />;
  }

  return currentUser ? children : <Navigate to="/login" />;
};

const LicenseWarningBanner = () => {
  const { licenseStatus } = useAuth();

  if (!licenseStatus?.inGracePeriod) return null;

  return (
    <div className="license-warning-banner">
      <span className="warning-icon">⚠️</span>
      <span className="warning-text">
        Your subscription expired. You have <strong>{licenseStatus.graceDaysRemaining} days</strong> remaining
        in grace period. Please renew to avoid service interruption.
      </span>
    </div>
  );
};

const RoleBasedRedirect = ({ children }) => {
  const { userRole } = useAuth();
  const location = useLocation();

  // Only redirect on root path
  if (location.pathname === '/' && userRole === 'cashier') {
    return <Navigate to="/cashier" replace />;
  }

  return children;
};

const AppContent = () => {
  const location = useLocation();
  const hideNavbarRoutes = ['/', '/login'];
  const shouldShowNavbar = !hideNavbarRoutes.includes(location.pathname);

  return (
    <div className="App">
      <LicenseWarningBanner />
      {shouldShowNavbar && <Navbar />}
      <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <RoleBasedRedirect>
                    <UserOrderView />
                  </RoleBasedRedirect>
                </PrivateRoute>
              }
            />
            <Route
              path="/cashier"
              element={
                <PrivateRoute>
                  <CashierView />
                </PrivateRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <SalesReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/sessions"
              element={
                <PrivateRoute>
                  <SessionsView />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Analytics />
                </PrivateRoute>
              }
            />
            <Route
              path="/products"
              element={
                <PrivateRoute>
                  <ProductManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <PrivateRoute>
                  <InventoryManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/setup"
              element={
                <PrivateRoute>
                  <SetupData />
                </PrivateRoute>
              }
            />
            <Route
              path="/user-roles"
              element={
                <PrivateRoute>
                  <UserRoleManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
